import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { ArticleAutoRefreshInputDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { ArticleDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';

/**
 * Per-article "Auto-refresh URL" toggle. Optimistic update, toasts and cache
 * invalidation live in the shared mutation defaults; `togglingIds` tracks which
 * rows have a toggle in flight so each switch can disable itself independently.
 */
export const useArticleAutoRefresh = () => {
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const { mutate } = useMutation<ArticleDto, unknown, ArticleAutoRefreshInputDto>({
    mutationKey: [QueryKey.knowledgeBase.articles.autoRefresh],
  });

  const toggle = useCallback(
    (articleId: string | number, enabled: boolean) => {
      // The API serializes article ids as numbers, but the article query is
      // keyed by the string route param. Coerce so the optimistic cache write
      // lands on the right key (otherwise the sheet won't reflect the toggle).
      const id = String(articleId);
      setTogglingIds((prev) => new Set(prev).add(id));
      mutate(
        { articleId: id, enabled },
        {
          onSettled: () =>
            setTogglingIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            }),
        },
      );
    },
    [mutate],
  );

  return { toggle, togglingIds };
};
